import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, ExternalLink, Plug, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { adminApi, errorMessage } from "@/lib/api";
import { formatPrice } from "@/lib/site";
import { PrintFileField } from "@/components/admin/PrintFileField";

const STATUS = {
  ready: { l: "Pronto per la stampa", c: "bg-acid text-ink" },
  draft_orders: { l: "Manca il file di stampa · ordini in bozza", c: "bg-amber-300/20 text-amber-200" },
  missing_price: { l: "Manca il prezzo · nascosto", c: "bg-red-500/15 text-red-300" },
  hidden: { l: "Nascosto", c: "bg-ink-3 text-smoke" },
};
const field = "h-9 w-full border border-line bg-ink px-2 text-xs outline-none focus:border-acid";
const fileUrl = (files, type) => files.find((f) => f.type === type)?.url || "";

const ConnectionCard = ({ data, onSync, onDemo, busy }) => (
  <div className="grid gap-px border border-line bg-line md:grid-cols-3" data-testid="gelato-status-card">
    <div className="bg-ink-2 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke"><Plug className="h-3.5 w-3.5" /> Connessione Gelato</p>
      <p className={`mt-3 flex items-center gap-2 text-sm ${data.live ? "text-acid" : "text-amber-200"}`} data-testid="gelato-connection-status">
        {data.live ? <CheckCircle2 className="h-4 w-4" /> : <CircleAlert className="h-4 w-4" />}
        {data.live ? "Collegato con API key" : "API key non ancora collegata"}
      </p>
      {!data.live && <p className="mt-2 text-xs leading-relaxed text-smoke">Aggiungi GELATO_API_KEY e GELATO_STORE_ID per importare il catalogo reale e inviare gli ordini.</p>}
    </div>
    <div className="bg-ink-2 p-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">Modalità demo</p>
      <label className="mt-3 flex items-center gap-3 text-sm">
        <Switch checked={data.demo && !data.live} disabled={data.live || busy} onCheckedChange={onDemo} data-testid="gelato-demo-switch" />
        {data.live ? "Non serve · catalogo reale collegato" : data.demo ? "Attiva · prodotti di esempio" : "Spenta"}
      </label>
      <p className="mt-2 text-xs text-smoke">Vale solo per questo ambiente: i dati di esempio non arrivano sul sito online.</p>
    </div>
    <div className="bg-ink-2 p-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">Sincronizzazione automatica · ogni 60 s</p>
      <p className="mt-3 text-sm" data-testid="gelato-last-sync">
        {data.last_sync_at ? `${new Date(data.last_sync_at).toLocaleString("it-IT")} · ${data.last_sync_count} prodotti` : "Mai"}
      </p>
      {data.last_sync_error && <p className="mt-1 text-xs text-red-300" data-testid="gelato-sync-error">{data.last_sync_error}</p>}
      <button onClick={onSync} disabled={busy} data-testid="gelato-sync-button" className="mt-3 inline-flex h-9 items-center gap-2 border border-line px-3 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-acid hover:text-acid disabled:opacity-50">
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Sincronizza ora
      </button>
    </div>
  </div>
);

const ProductInfo = ({ p }) => {
  const st = STATUS[p.status];
  const linked = p.linked_to;
  return (
    <>
      <td className="p-4"><img src={linked?.image || p.preview_url} alt="" className="h-16 w-16 object-cover" /></td>
      <td className="max-w-[260px] p-4">
        <p className="font-display text-sm font-bold uppercase" data-testid="gelato-product-name">{linked?.name || p.title}</p>
        {linked && <p className="mt-1 text-[11px] leading-snug text-smoke" data-testid="gelato-product-linked">Su Gelato: {p.title}</p>}
        <p className="mt-1 font-mono text-[10px] text-smoke">{linked ? (linked.sizes.length > 1 ? `Taglie ${linked.sizes.join(" · ")}` : "Formato unico") : `${p.variants} varianti`}{p.demo ? " · esempio" : ""}</p>
        <Link to={`/prodotto/${linked?.id || p.slug}`} target="_blank" className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cream/80 hover:text-acid">Pagina <ExternalLink className="h-3 w-3" /></Link>
        <p className="mt-2"><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-[0.12em] ${st.c}`} data-testid="gelato-product-status">{st.l}</span></p>
      </td>
    </>
  );
};

const ProductRow = ({ p, onSaved }) => {
  const [form, setForm] = useState({ price: p.price ?? "", visible: p.visible, front: fileUrl(p.print_files, "default"), back: fileUrl(p.print_files, "back"), tagline: p.tagline });
  const [saving, setSaving] = useState(false);
  const linked = Boolean(p.linked_to);

  const save = async () => {
    setSaving(true);
    const print_files = [["default", form.front], ["back", form.back]].filter(([, url]) => url.trim()).map(([type, url]) => ({ type, url: url.trim() }));
    const body = linked ? { print_files } : { price: form.price === "" ? null : Number(form.price), visible: form.visible, tagline: form.tagline, badge: "Print on demand", print_files };
    try {
      await adminApi.put(`/admin/gelato/products/${p.gelato_id}`, body);
      toast.success("Prodotto aggiornato", { description: p.linked_to?.name || p.title });
      await onSaved();
    } catch (err) {
      toast.error(errorMessage(err, "Controlla prezzo e link dei file (devono iniziare con https://)."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-b border-line align-top last:border-b-0" data-testid={`gelato-product-row-${p.linked_to?.id || p.slug}`}>
      <ProductInfo p={p} />
      <td className="w-32 p-4">
        {linked ? <p className="font-mono text-xs" data-testid="gelato-price-linked">{formatPrice(p.price)}<span className="mt-1 block text-[10px] uppercase tracking-[0.12em] text-smoke">dal sito</span></p>
          : <input type="number" min="1" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="€" data-testid="gelato-price-input" className={field} />}
      </td>
      <td className="p-4">
        {linked ? <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-acid">Sul sito</p>
          : <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="gelato-visible-switch" />}
      </td>
      <td className="min-w-[380px] space-y-3 p-4">
        {!linked && <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Sottotitolo (es. Cotone 220 gsm)" data-testid="gelato-tagline-input" className={field} />}
        <PrintFileField label="File di stampa · fronte" value={form.front} onChange={(front) => setForm((f) => ({ ...f, front }))} testid="gelato-file-front" />
        {p.linked_to?.sizes.length !== 1 && <PrintFileField label="Retro · facoltativo" value={form.back} onChange={(back) => setForm((f) => ({ ...f, back }))} testid="gelato-file-back" />}
      </td>
      <td className="p-4">
        <button onClick={save} disabled={saving} data-testid="gelato-save-button" className="h-9 bg-acid px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-ink disabled:opacity-60">{saving ? "Salvo…" : "Salva"}</button>
      </td>
    </tr>
  );
};

export const GelatoPanel = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-gelato"], queryFn: () => adminApi.get("/admin/gelato").then((r) => r.data) });
  const [busy, setBusy] = useState(false);

  const refreshAll = (next) => {
    queryClient.setQueryData(["admin-gelato"], next);
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };
  const run = async (request, message) => {
    setBusy(true);
    try {
      refreshAll((await request()).data);
      toast.success(message);
    } catch (err) {
      toast.error(errorMessage(err, "Operazione non riuscita."));
    } finally {
      setBusy(false);
    }
  };
  const onSaved = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-gelato"] });
    await queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  if (isLoading || !data) return <p className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">Caricamento…</p>;
  const products = Array.isArray(data.products) ? data.products : [];
  return (
    <div className="space-y-8" data-testid="gelato-panel">
      <ConnectionCard
        data={data}
        busy={busy}
        onSync={() => run(() => adminApi.post("/admin/gelato/sync"), "Catalogo sincronizzato")}
        onDemo={(demo) => run(() => adminApi.put("/admin/gelato/settings", { demo }), demo ? "Modalità demo attiva" : "Modalità demo spenta")}
      />
      <p className="text-sm leading-relaxed text-bone/60">
        I prodotti del sito collegati a Gelato mantengono nome, foto, testi e prezzo del sito: gli ordini pagati partono da soli verso Gelato con la taglia scelta. Carica il file di stampa (lo stesso PNG usato nel tuo negozio Gelato): con il file l&apos;ordine va subito in produzione, senza file arriva su Gelato in bozza. Quando Gelato spedisce, il cliente riceve un&apos;email con il tracking.
      </p>
      {data.live && data.live_payments && !Object.values(data.live_payments).every(Boolean) && (
        <p className="border-l-2 border-amber-300 bg-ink-2 px-4 py-3 text-sm text-amber-100/90" data-testid="gelato-test-payments-note">
          Pagamenti in modalità di prova: gli ordini di test arrivano su Gelato sempre in bozza, quindi niente stampa e nessun addebito. Puoi eliminarli dalla dashboard Gelato.
        </p>
      )}
      <div className="overflow-x-auto border border-line">
        {!products.length ? (
          <p className="p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-smoke" data-testid="gelato-empty">Nessun prodotto Gelato sincronizzato</p>
        ) : (
          <table className="w-full min-w-[1120px] text-sm" data-testid="gelato-products-table">
            <thead className="border-b border-line bg-ink-2">
              <tr>{["", "Prodotto", "Prezzo", "Visibile", "File di stampa", ""].map((h, i) => <th key={i} className="p-4 text-left font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-smoke">{h}</th>)}</tr>
            </thead>
            <tbody>{products.map((p) => <ProductRow key={`${p.gelato_id}-${p.price}-${p.visible}-${p.print_files.map((f) => f.url).join()}`} p={p} onSaved={onSaved} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};
